import { gsap } from 'gsap';
import { products, type ProductId } from '../presentation/catalogue';
import { BRANDS } from '../brand/brands';
import { brandLogo } from './brandLogo';
import { chars } from './text';

/**
 * The branded payoff at the end of every "problem first" sequence. The product is named by its
 * master logo, never by retyped text. MINT branches are the exception while their identities are
 * pending owner approval: they show the unaltered MINT master as the parent mark and the branch
 * name in the neutral showcase face, with no proposed branch symbol, colour or font.
 */
export function productReveal(id: ProductId, from: number, className = '', until?: number): string {
  const product = products[id];
  const identity = BRANDS[product.brand];
  const pending = identity.status === 'pending-approval';
  const eyebrow = product.tier === 'specialisation' ? '' : product.tier === 'selected' ? 'Selected work' : 'Moderta';
  const range = `data-from="${from}"${until === undefined ? '' : ` data-until="${until}"`}`;

  const mark = pending
    ? `<span class="product-reveal__parent">${brandLogo('mint', { height: 26 })}</span>
       <h2 class="product-reveal__name" aria-label="${product.name}">${chars(product.name)}</h2>`
    : `<h2 class="product-reveal__logo" aria-label="${identity.name}">${brandLogo(product.brand, { height: logoHeight(product.brand) })}</h2>`;

  return `
    <div class="product-reveal ${className}" ${range} data-brand-reveal="${product.brand}">
      ${eyebrow ? `<span class="product-reveal__family">${eyebrow}</span>` : ''}
      ${mark}
      <span class="product-reveal__rule" aria-hidden="true"></span>
      <p class="product-reveal__tagline">${product.tagline}</p>
    </div>`;
}

/** Optical sizes: each master has different proportions, so equal heights would not read as equal weight. */
function logoHeight(id: string): number {
  switch (id) {
    case 'research-space': return 120;
    case 'native': return 132;
    case 'carbonmove': return 92;
    case 'climateviewpoint': return 96;
    // The eTrust master is a 176×46 raster: shown close to its native height (vector master requested).
    case 'etrust': return 58;
    default: return 110;
  }
}

/** Logo reveal: a soft mask sweep left to right, then the rule draws and the tagline settles. */
export function playProductReveal(root: Element, tl: gsap.core.Timeline, at = 0): void {
  const reveal = root.querySelector('.product-reveal');
  if (!reveal) return;
  const family = reveal.querySelector('.product-reveal__family');
  const logo = reveal.querySelector('.product-reveal__logo');
  const rule = reveal.querySelector('.product-reveal__rule');
  if (family) tl.fromTo(family, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, at);
  if (logo) {
    tl.fromTo(
      logo,
      { opacity: 0, clipPath: 'inset(0 100% 0 0)', y: 10, filter: 'blur(6px)' },
      { opacity: 1, clipPath: 'inset(0 0% 0 0)', y: 0, filter: 'blur(0px)', duration: 1.3, ease: 'expo.out' },
      at + 0.15,
    );
  }
  const letters = reveal.querySelectorAll('.char');
  if (letters.length) {
    tl.fromTo(reveal.querySelector('.product-reveal__parent'), { opacity: 0 }, { opacity: 1, duration: 0.6 }, at);
    tl.fromTo(letters, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.9, stagger: { each: 0.035, from: 'start' }, ease: 'power3.out' }, at + 0.15);
  }
  if (rule) tl.fromTo(rule, { scaleX: 0 }, { scaleX: 1, duration: 0.9, ease: 'expo.inOut' }, at + 0.65);
  tl.fromTo(reveal.querySelector('.product-reveal__tagline'), { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' }, at + 0.85);
}

export function settleProductReveal(root: Element): void {
  const reveal = root.querySelector('.product-reveal');
  if (!reveal) return;
  gsap.set(reveal.querySelectorAll('.product-reveal__family, .product-reveal__parent, .char, .product-reveal__tagline'), { opacity: 1, y: 0, filter: 'blur(0px)' });
  gsap.set(reveal.querySelectorAll('.product-reveal__logo'), { opacity: 1, y: 0, filter: 'blur(0px)', clipPath: 'inset(0 0% 0 0)' });
  gsap.set(reveal.querySelectorAll('.product-reveal__rule'), { scaleX: 1 });
}
